import { useState, useRef, useEffect } from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import { Calendar03Icon, ArrowRight01Icon, Cancel01Icon } from '@hugeicons/core-free-icons';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

function isSameDay(a, b) {
    return a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function toLocalDate(str) {
    if (!str) return null;
    const [y, m, d] = str.split('-').map(Number);
    return new Date(y, m - 1, d);
}

function toDateStr(d) {
    if (!d) return '';
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function formatDisplay(str) {
    if (!str) return null;
    const d = toLocalDate(str);
    return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

function CalendarMonth({ year, month, start, end, hovered, onDayClick, onDayHover }) {
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];

    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));

    const startD = toLocalDate(start);
    const endD = toLocalDate(end);
    const hoverD = hovered;
    const rangeEnd = endD || hoverD;

    return (
        <div className="w-64">
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-200 text-center mb-3">
                {MONTHS[month]} {year}
            </p>
            <div className="grid grid-cols-7 mb-1">
                {DAYS.map(d => (
                    <span key={d} className="text-center text-[10px] font-medium text-gray-400 dark:text-gray-500 py-1">{d}</span>
                ))}
            </div>
            <div className="grid grid-cols-7">
                {cells.map((day, i) => {
                    if (!day) return <div key={`e-${i}`} />;

                    const isStart = isSameDay(day, startD);
                    const isEnd = isSameDay(day, endD);
                    const inRange = startD && rangeEnd && day > startD && day < rangeEnd;
                    const today = new Date(); today.setHours(0,0,0,0);
                    const isFuture = day > today;

                    let dayClass = 'relative flex items-center justify-center h-8 text-sm cursor-pointer select-none transition-colors ';
                    let bgClass = '';

                    if (isStart || isEnd) {
                        dayClass += 'text-white font-semibold z-10 ';
                        bgClass = 'absolute inset-0 rounded-full bg-[#1e3a6e]';
                    } else if (inRange) {
                        dayClass += 'text-[#1e3a6e] dark:text-blue-300 ';
                        bgClass = 'absolute inset-0 bg-blue-50 dark:bg-blue-500/10';
                    } else if (isFuture) {
                        dayClass += 'text-gray-300 cursor-not-allowed ';
                    } else {
                        dayClass += 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full ';
                    }

                    return (
                        <div
                            key={i}
                            className={dayClass}
                            onClick={() => !isFuture && onDayClick(day)}
                            onMouseEnter={() => !isFuture && onDayHover(day)}
                        >
                            {bgClass && <span className={bgClass} />}
                            <span className="relative z-10">{day.getDate()}</span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function DateFilter({ startDate, setStartDate, endDate, setEndDate, filterPayments, processing }) {

    const [open, setOpen] = useState(false);
    const [selecting, setSelecting] = useState('start');
    const [hovered, setHovered] = useState(null);
    const [leftMonth, setLeftMonth] = useState(() => {
        const d = toLocalDate(startDate) || new Date();
        return { year: d.getFullYear(), month: d.getMonth() };
    });
    const ref = useRef(null);

    const rightMonth = leftMonth.month === 11
        ? { year: leftMonth.year + 1, month: 0 }
        : { year: leftMonth.year, month: leftMonth.month + 1 };

    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const prevMonth = () => {
        setLeftMonth(prev => prev.month === 0
            ? { year: prev.year - 1, month: 11 }
            : { year: prev.year, month: prev.month - 1 });
    };

    const nextMonth = () => {
        setLeftMonth(prev => prev.month === 11
            ? { year: prev.year + 1, month: 0 }
            : { year: prev.year, month: prev.month + 1 });
    };

    const handleDayClick = (day) => {
        if (selecting === 'start') {
            setStartDate(toDateStr(day));
            setEndDate('');
            setSelecting('end');
        } else {
            const startD = toLocalDate(startDate);
            if (startD && day < startD) {
                setStartDate(toDateStr(day));
                setEndDate('');
                setSelecting('end');
            } else {
                setEndDate(toDateStr(day));
                setSelecting('start');
                setOpen(false);
            }
        }
    };

    const handleClear = (e) => {
        e.stopPropagation();
        const today = toDateStr(new Date());
        setStartDate(today);
        setEndDate(today);
        setSelecting('start');
    };

    const hasRange = startDate && endDate && startDate !== endDate;
    const label = startDate && endDate
        ? startDate === endDate
            ? formatDisplay(startDate)
            : `${formatDisplay(startDate)} → ${formatDisplay(endDate)}`
        : 'Select date range';

    return (
        <div className="flex items-center gap-3">
            <div className="relative" ref={ref}>
                <button
                    type="button"
                    onClick={() => setOpen(o => !o)}
                    className={`inline-flex items-center gap-2.5 px-4 py-2.5 rounded-lg border text-sm transition-colors ${
                        open
                            ? 'border-[#1e3a6e] dark:border-blue-500 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 ring-2 ring-[#1e3a6e]/20 dark:ring-blue-500/20'
                            : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                >
                    <HugeiconsIcon icon={Calendar03Icon} size={15} className="text-gray-400 shrink-0" />
                    <span className={hasRange || (startDate && endDate) ? 'text-gray-800 dark:text-gray-100 font-medium' : 'text-gray-400 dark:text-gray-500'}>
                        {label}
                    </span>
                    {hasRange && (
                        <span
                            onClick={handleClear}
                            className="ml-1 p-0.5 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600"
                        >
                            <HugeiconsIcon icon={Cancel01Icon} size={12} />
                        </span>
                    )}
                </button>

                {open && (
                    <div className="absolute right-0 top-full mt-2 z-50 bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-xl p-5">
                        <p className="text-xs font-medium text-gray-400 dark:text-gray-500 mb-4 text-center">
                            {selecting === 'start' ? 'Select check-in date' : 'Select check-out date'}
                        </p>

                        <div className="flex items-start gap-6">
                            {/* Left month nav */}
                            <div>
                                <div className="flex items-center justify-between mb-3">
                                    <button type="button" onClick={prevMonth} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 transition-colors">
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 18l-6-6 6-6"/></svg>
                                    </button>
                                    <div className="w-40" />
                                </div>
                                <CalendarMonth
                                    year={leftMonth.year}
                                    month={leftMonth.month}
                                    start={startDate}
                                    end={endDate}
                                    hovered={hovered}
                                    onDayClick={handleDayClick}
                                    onDayHover={setHovered}
                                />
                            </div>

                            {/* Divider */}
                            <div className="w-px bg-gray-100 dark:bg-gray-700 self-stretch mx-1" />

                            {/* Right month nav */}
                            <div>
                                <div className="flex items-center justify-between mb-3">
                                    <div className="w-40" />
                                    <button type="button" onClick={nextMonth} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 transition-colors">
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>
                                    </button>
                                </div>
                                <CalendarMonth
                                    year={rightMonth.year}
                                    month={rightMonth.month}
                                    start={startDate}
                                    end={endDate}
                                    hovered={hovered}
                                    onDayClick={handleDayClick}
                                    onDayHover={setHovered}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <button
                type="button"
                onClick={filterPayments}
                disabled={processing || !startDate || !endDate}
                className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-[#1e3a6e] rounded-lg hover:bg-[#264d91] disabled:opacity-50 disabled:cursor-not-allowed transition-colors shrink-0"
            >
                {processing ? (
                    <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                    </svg>
                ) : (
                    <HugeiconsIcon icon={ArrowRight01Icon} size={15} />
                )}
                Filter
            </button>
        </div>
    );
}

export default DateFilter;
